import type { ComponentType } from 'react'
import { TbDiamond, TbDroplet, TbFlame, TbLeaf, TbMountain } from 'react-icons/tb'
import type { FourPillarsChart, OHeng, OHengDistribution, OHengKey } from '../model/types'

export const OH_HENG_MAP: Record<OHeng, OHengKey> = {
  木: 'wood',
  火: 'fire',
  土: 'earth',
  金: 'metal',
  水: 'water',
}

export const OH_HENG_KOREAN: Record<OHengKey, string> = {
  wood: '목',
  fire: '화',
  earth: '토',
  metal: '금',
  water: '수',
}

export const OH_HENG_HANJA: Record<OHengKey, string> = {
  wood: '木',
  fire: '火',
  earth: '土',
  metal: '金',
  water: '水',
}

export const OH_HENG_ICONS: Record<OHengKey, ComponentType<{ className?: string }>> = {
  wood: TbLeaf,
  fire: TbFlame,
  earth: TbMountain,
  metal: TbDiamond,
  water: TbDroplet,
}

export const OH_HENG_COLORS: Record<OHengKey, string> = {
  wood: '#35C9A0',
  fire: '#E04E78',
  earth: '#EBB03D',
  metal: '#A8B4C8',
  water: '#4A90D9',
}

export const OH_HENG_MUTED_COLORS: Record<OHengKey, string> = {
  wood: '#122B24',
  fire: '#2E1520',
  earth: '#2B2112',
  metal: '#232730',
  water: '#141E2E',
}

/**
 * Count oh-heng elements from fourPillarsChart.
 * 4 pillars x 2 (cheonGan + jiJi) = max 8 positions.
 * If hourPillar is null, 6 positions.
 */
export function computeOHengDistribution(chart: FourPillarsChart): OHengDistribution {
  const counts: OHengDistribution = { wood: 0, fire: 0, earth: 0, metal: 0, water: 0 }

  if (!chart?.yearPillar) {
    return counts
  }

  const pillars = [chart.yearPillar, chart.monthPillar, chart.dayPillar]
  if (chart.hourPillar) {
    pillars.push(chart.hourPillar)
  }

  for (const pillar of pillars) {
    const cKey = OH_HENG_MAP[pillar.cheonGan.oHeng]
    const jKey = OH_HENG_MAP[pillar.jiJi.oHeng]
    if (cKey) {
      counts[cKey]++
    }
    if (jKey) {
      counts[jKey]++
    }
  }

  return counts
}

/**
 * Convert distribution counts to percentages.
 */
export function computeOHengPercentages(dist: OHengDistribution): OHengDistribution {
  const total = dist.wood + dist.fire + dist.earth + dist.metal + dist.water
  if (total === 0) {
    return { wood: 0, fire: 0, earth: 0, metal: 0, water: 0 }
  }

  return {
    wood: Math.round((dist.wood / total) * 100),
    fire: Math.round((dist.fire / total) * 100),
    earth: Math.round((dist.earth / total) * 100),
    metal: Math.round((dist.metal / total) * 100),
    water: Math.round((dist.water / total) * 100),
  }
}

/** 12 시진 data */
export const SIJI_DATA = [
  { name: '자시', range: '23~01시', value: '23:00' },
  { name: '축시', range: '01~03시', value: '01:00' },
  { name: '인시', range: '03~05시', value: '03:00' },
  { name: '묘시', range: '05~07시', value: '05:00' },
  { name: '진시', range: '07~09시', value: '07:00' },
  { name: '사시', range: '09~11시', value: '09:00' },
  { name: '오시', range: '11~13시', value: '11:00' },
  { name: '미시', range: '13~15시', value: '13:00' },
  { name: '신시', range: '15~17시', value: '15:00' },
  { name: '유시', range: '17~19시', value: '17:00' },
  { name: '술시', range: '19~21시', value: '19:00' },
  { name: '해시', range: '21~23시', value: '21:00' },
] as const
